import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    IconButton,
    Tooltip,
    Chip,
    TablePagination,
    Stack,
    Autocomplete,
    TextField
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    SwapHoriz,
    History,
    FilterList,
    Warning as WarningIcon,
    Clear as ClearIcon,
    FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import centralStockAPI from '../../services/centralStockAPI';
import { branchAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import NoPrivilege from '../../components/common/NoPrivilege';
import { useAuth } from '../../context/AuthContext';
import AddCentralStockItem from './addCentralStockItem';
import TransferStockDialog from './transferStockDialog';
import SearchBar from '../../components/common/SearchBar';

const CentralStock = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, isSuperAdmin } = useAuth();

    // Filter options
    const SORT_BY_OPTIONS = [
        { value: 'name', label: t('common.name') },
        { value: 'quantity', label: t('common.quantity') },
        { value: 'price', label: t('common.price') },
        { value: 'averageCost', label: t('centralStock.avgCost', 'Avg Cost (WAC)') },
        { value: 'createdAt', label: t('common.createdAt') },
    ];

    const SORT_ORDER_OPTIONS = [
        { value: 'asc', label: t('common.ascending') },
        { value: 'desc', label: t('common.descending') },
    ];

    // --- State Management ---
    const [stock, setStock] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [openTransferDialog, setOpenTransferDialog] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    // Export State
    const [exporting, setExporting] = useState(false);

    // Consolidated filter state
    const [filters, setFilters] = useState({
        search: '',
        lowStock: false,
        expired: false,
        showInactive: false,
        sortBy: 'name',
        sortOrder: 'asc',
    });

    // --- Permissions ---
    const canView = isSuperAdmin || user?.privileges?.some(p => p.module === 'centralStock' && p.operations.includes('view'));
    const canCreate = isSuperAdmin || user?.privileges?.some(p => p.module === 'centralStock' && p.operations.includes('create'));
    const canUpdate = isSuperAdmin || user?.privileges?.some(p => p.module === 'centralStock' && p.operations.includes('update'));
    const canDelete = isSuperAdmin || user?.privileges?.some(p => p.module === 'centralStock' && p.operations.includes('delete'));
    const canExport = canView;

    // --- Data Fetching ---
    const fetchStock = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        try {
            const params = {
                page: page + 1,
                limit: rowsPerPage,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
            };

            if (filters.search) params.search = filters.search;
            if (filters.lowStock) params.lowStock = 'true';
            if (filters.expired) params.expired = 'true';

            const response = await centralStockAPI.getAll(params);

            setStock(response.data.data || []);
            setTotalCount(response.data.pagination?.total || 0);
        } catch (error) {
            console.error('Fetch stock error:', error);
            toast.error(error.response?.data?.message || t('centralStock.fetchError', 'Failed to fetch central stock'));
            setStock([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, filters, canView, t]);

    useEffect(() => {
        fetchStock();
    }, [fetchStock]);

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const response = await branchAPI.getActiveBranches();
                setBranches(response.data?.data || []);
            } catch (error) {
                toast.error(t('branches.fetchError', 'Failed to fetch branches'));
            }
        };
        if (canView) fetchBranches();
    }, [canView, t]);

    // --- Handlers ---
    const handleOpenDialog = (item = null) => {
        setSelectedItem(item);
        setOpenDialog(true);
    };

    const handleOpenTransferDialog = (item) => {
        setSelectedItem(item);
        setOpenTransferDialog(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('common.deleteConfirm'))) {
            try {
                await centralStockAPI.delete(id);
                toast.success(t('centralStock.deleteSuccess', 'Item deleted successfully'));
                fetchStock();
            } catch (error) {
                toast.error(error.response?.data?.message || t('centralStock.deleteError', 'Failed to delete item'));
            }
        }
    };

    const handleFilterChange = (key, value) => {
        setPage(0);
        setFilters(prev => {
            const newFilters = { ...prev, [key]: value };
            if (key === 'lowStock' && value) {
                newFilters.expired = false;
            } else if (key === 'expired' && value) {
                newFilters.lowStock = false;
            }
            return newFilters;
        });
    };

    const handleClearFilters = () => {
        setPage(0);
        setFilters({
            search: '',
            lowStock: false,
            expired: false,
            showInactive: false,
            sortBy: 'name',
            sortOrder: 'asc',
        });
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = {
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
            };

            if (filters.search) params.search = filters.search;
            if (filters.lowStock) params.lowStock = true;
            if (filters.expired) params.expired = true;

            const response = await centralStockAPI.export(params);

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const date = new Date().toISOString().split('T')[0];
            link.download = `central_stock_export_${date}.xlsx`;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success(t('centralStock.exportSuccess', 'Central stock exported successfully'));
        } catch (err) {
            console.error('Export error:', err);
            toast.error(
                err.response?.data?.message ||
                t('centralStock.exportError', 'Failed to export central stock')
            );
        } finally {
            setExporting(false);
        }
    };

    // Helper functions
    const isLowStock = (item) => item.quantity <= item.minimumThreshold;
    const isExpired = (item) => item.validUntil && new Date(item.validUntil) < new Date();
    const hasActiveFilters = filters.search || filters.lowStock || filters.expired || filters.showInactive;

    /**
     * Format the average cost with appropriate styling
     */
    const formatAvgCost = (item) => {
        const avgCost = item.averageCost;
        if (avgCost === undefined || avgCost === null || avgCost === 0) {
            return { display: '—', color: 'text.secondary' };
        }
        return {
            display: avgCost.toFixed(2),
            color: 'text.primary'
        };
    };

    /**
     * Calculate total stock value (quantity × averageCost)
     */
    const getStockValue = (item) => {
        if (!item.averageCost || item.averageCost === 0) return null;
        return (item.quantity * item.averageCost).toFixed(2);
    };

    if (!canView) return <NoPrivilege message={t('noPrivilege')} />;

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h4" component="h1">{t('centralStock.title')}</Typography>
                    {!loading && (
                        <Chip
                            label={`${totalCount} ${t('common.total', 'total')}`}
                            color="primary"
                            variant="outlined"
                            size="small"
                        />
                    )}
                </Box>
                <Stack direction="row" spacing={1}>
                    {canExport && (
                        <Button
                            variant="outlined"
                            startIcon={exporting ? <CircularProgress size={18} /> : <FileDownloadIcon />}
                            onClick={handleExport}
                            disabled={exporting || loading}
                        >
                            {exporting
                                ? t('common.exporting', 'Exporting...')
                                : t('common.export', 'Export')
                            }
                        </Button>
                    )}
                    {canCreate && (
                        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
                            {t('centralStock.addTitle')}
                        </Button>
                    )}
                </Stack>
            </Box>

            {/* Filters Paper */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Stack direction="row" alignItems="center" gap={2} mb={2}>
                    <FilterList />
                    <Typography variant="h6">{t('filters', 'Filters')}</Typography>
                    {hasActiveFilters && (
                        <Chip
                            label={t('common.filtersApplied', 'Filters will be applied to export')}
                            size="small"
                            color="info"
                            variant="outlined"
                        />
                    )}
                </Stack>

                {/* Search and Sort Row */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
                    <SearchBar
                        onSearch={(query) => handleFilterChange('search', query)}
                        initialValue={filters.search}
                        sx={{ flexGrow: 1, minWidth: '250px' }}
                    />
                    <Autocomplete
                        options={SORT_BY_OPTIONS}
                        getOptionLabel={(option) => t(`centralStock.sortOptions.${option.value}`, option.label)}
                        value={SORT_BY_OPTIONS.find(opt => opt.value === filters.sortBy)}
                        onChange={(event, newValue) => handleFilterChange('sortBy', newValue?.value || 'name')}
                        renderInput={(params) => <TextField {...params} label={t('common.sortBy')} size="small" />}
                        sx={{ minWidth: 180 }}
                        disableClearable
                    />
                    <Autocomplete
                        options={SORT_ORDER_OPTIONS}
                        getOptionLabel={(option) => t(`common.${option.value}`, option.label)}
                        value={SORT_ORDER_OPTIONS.find(opt => opt.value === filters.sortOrder)}
                        onChange={(event, newValue) => handleFilterChange('sortOrder', newValue?.value || 'asc')}
                        renderInput={(params) => <TextField {...params} label={t('common.order')} size="small" />}
                        sx={{ minWidth: 150 }}
                        disableClearable
                    />

                    {/* Low Stock Filter */}
                    <Button
                        variant={filters.lowStock ? 'contained' : 'outlined'}
                        color="warning"
                        startIcon={<WarningIcon />}
                        onClick={() => handleFilterChange('lowStock', !filters.lowStock)}
                        size="small"
                    >
                        {t('centralStock.lowStock', 'Low Stock')}
                    </Button>

                    {/* Expired Filter */}
                    <Button
                        variant={filters.expired ? 'contained' : 'outlined'}
                        color="error"
                        startIcon={<WarningIcon />}
                        onClick={() => handleFilterChange('expired', !filters.expired)}
                        size="small"
                    >
                        {t('centralStock.expired', 'Expired')}
                    </Button>

                    {/* Clear Filters */}
                    {hasActiveFilters && (
                        <Button
                            variant="text"
                            color="inherit"
                            onClick={handleClearFilters}
                            startIcon={<ClearIcon />}
                            size="small"
                        >
                            {t('common.clearFilters', 'Clear')}
                        </Button>
                    )}
                </Stack>
            </Paper>

            {/* Active Filters Display */}
            {hasActiveFilters && (
                <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    {filters.lowStock && (
                        <Chip
                            label={t('centralStock.lowStock', 'Low Stock')}
                            color="warning"
                            size="small"
                            onDelete={() => handleFilterChange('lowStock', false)}
                        />
                    )}
                    {filters.expired && (
                        <Chip
                            label={t('centralStock.expired', 'Expired')}
                            color="error"
                            size="small"
                            onDelete={() => handleFilterChange('expired', false)}
                        />
                    )}
                    {filters.showInactive && (
                        <Chip
                            label={t('centralStock.includingInactive', 'Including Inactive')}
                            color="info"
                            size="small"
                            onDelete={() => handleFilterChange('showInactive', false)}
                        />
                    )}
                    {filters.search && (
                        <Chip
                            label={`${t('common.search', 'Search')}: ${filters.search}`}
                            size="small"
                            onDelete={() => handleFilterChange('search', '')}
                        />
                    )}
                </Box>
            )}

            {/* Results Info */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('common.showing', 'Showing')} {totalCount === 0 ? 0 : Math.min(page * rowsPerPage + 1, totalCount)}-{Math.min((page + 1) * rowsPerPage, totalCount)} {t('common.of', 'of')} {totalCount} {t('common.items', 'items')}
            </Typography>

            {/* Table */}
            <Paper sx={{ width: '100%' }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('centralStock.name')}</TableCell>
                                <TableCell>{t('centralStock.category')}</TableCell>
                                <TableCell align="center">{t('centralStock.quantity')}</TableCell>
                                <TableCell>{t('centralStock.unit')}</TableCell>
                                <TableCell align="center">{t('centralStock.threshold')}</TableCell>
                                <TableCell align="right">{t('centralStock.price')}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title={t('centralStock.avgCostTooltip', 'Weighted Average Cost — updated automatically when orders are approved')}>
                                        <span>{t('centralStock.avgCost', 'Avg Cost (WAC)')}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title={t('centralStock.stockValueTooltip', 'Total stock value = Quantity × Average Cost')}>
                                        <span>{t('centralStock.stockValue', 'Stock Value')}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>{t('centralStock.validUntil', 'Valid Until')}</TableCell>
                                <TableCell align="center">{t('centralStock.wacChanges', 'WAC Changes')}</TableCell>
                                <TableCell align="center">{t('centralStock.status')}</TableCell>
                                <TableCell>{t('centralStock.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : stock.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">
                                            {filters.search
                                                ? t('centralStock.noSearchResults', 'No items match your search')
                                                : filters.lowStock
                                                    ? t('centralStock.noLowStockItems', 'No low stock items found')
                                                    : filters.expired
                                                        ? t('centralStock.noExpiredItems', 'No expired items found')
                                                        : t('common.noResults', 'No items found')
                                            }
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                stock.map((item) => {
                                    const itemIsLowStock = isLowStock(item);
                                    const itemIsExpired = isExpired(item);
                                    const avgCostInfo = formatAvgCost(item);
                                    const stockValue = getStockValue(item);

                                    return (
                                        <TableRow
                                            key={item._id}
                                            hover
                                            sx={{
                                                backgroundColor: itemIsExpired
                                                    ? 'rgba(244, 67, 54, 0.08)'
                                                    : itemIsLowStock
                                                        ? 'rgba(255, 152, 0, 0.08)'
                                                        : 'inherit',
                                                opacity: item.isActive === false ? 0.6 : 1
                                            }}
                                        >
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {item.name}
                                                    </Typography>
                                                    {item.isActive === false && (
                                                        <Chip
                                                            label={t('common.inactive', 'Inactive')}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{item.category?.name || '-'}</TableCell>
                                            <TableCell align="center">
                                                <Typography
                                                    variant="body2"
                                                    color={itemIsLowStock ? 'warning.main' : 'inherit'}
                                                    fontWeight="bold"
                                                >
                                                    {item.quantity}
                                                    {itemIsLowStock && (
                                                        <WarningIcon
                                                            fontSize="small"
                                                            color="warning"
                                                            sx={{ ml: 0.5, verticalAlign: 'middle' }}
                                                        />
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{item.unit}</TableCell>
                                            <TableCell align="center">{item.minimumThreshold}</TableCell>
                                            <TableCell align="right">
                                                {item.price ? `${item.price.toFixed(2)}` : '-'}
                                            </TableCell>

                                            {/* ── Average Cost (WAC) ── */}
                                            <TableCell align="right">
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={avgCostInfo.display !== '—' ? 600 : 400}
                                                    color={avgCostInfo.color}
                                                >
                                                    {avgCostInfo.display}
                                                </Typography>
                                            </TableCell>

                                            {/* ── Stock Value ── */}
                                            <TableCell align="right">
                                                {stockValue ? (
                                                    <Typography variant="body2" fontWeight={500} color="primary.main">
                                                        {parseFloat(stockValue).toLocaleString()}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">—</Typography>
                                                )}
                                            </TableCell>

                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {item.validUntil
                                                        ? format(new Date(item.validUntil), 'dd/MM/yyyy')
                                                        : '-'
                                                    }
                                                    {itemIsExpired && (
                                                        <Chip
                                                            label={t('centralStock.expired', 'Expired')}
                                                            size="small"
                                                            color="error"
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>

                                            {/* ── WAC Changes Count ── */}
                                            <TableCell align="center">
                                                {item.wacHistory && item.wacHistory.length > 0 ? (
                                                    <Chip
                                                        label={item.wacHistory.length}
                                                        size="small"
                                                        color="info"
                                                        variant="outlined"
                                                    />
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">—</Typography>
                                                )}
                                            </TableCell>

                                            <TableCell align="center">
                                                <Stack direction="row" spacing={0.5} justifyContent="center">
                                                    {itemIsLowStock && !filters.lowStock && (
                                                        <Chip
                                                            label={t('centralStock.lowStock', 'Low')}
                                                            color="warning"
                                                            size="small"
                                                        />
                                                    )}
                                                    {!itemIsLowStock && !itemIsExpired && item.isActive !== false && (
                                                        <Chip
                                                            label={t('centralStock.inStock', 'In Stock')}
                                                            color="success"
                                                            size="small"
                                                        />
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5}>
                                                    <Tooltip title={t('centralStock.viewHistory')}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => navigate(`/central-stock/${item._id}/history`)}
                                                        >
                                                            <History />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {canUpdate && (
                                                        <Tooltip title={t('centralStock.edit')}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenDialog(item)}
                                                            >
                                                                <Edit />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {canUpdate && (
                                                        <Tooltip title={t('centralStock.transferToBranch')}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenTransferDialog(item)}
                                                                disabled={item.quantity === 0}
                                                            >
                                                                <SwapHoriz />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {canDelete && (
                                                        <Tooltip title={t('centralStock.delete')}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDelete(item._id)}
                                                            >
                                                                <Delete color="error" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            <Paper sx={{ mt: 2 }}>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    labelRowsPerPage={t('common.rowsPerPage')}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                />
            </Paper>

            {/* Dialogs */}
            <AddCentralStockItem
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                selectedItem={selectedItem}
                onSuccess={fetchStock}
            />
            <TransferStockDialog
                open={openTransferDialog}
                onClose={() => setOpenTransferDialog(false)}
                selectedItem={selectedItem}
                branches={branches}
                onSuccess={fetchStock}
            />
        </Box>
    );
};

export default CentralStock;